import React, { useState } from 'react';
import { Eye, RefreshCw, Filter, Settings } from 'lucide-react';
import './LayoutExamples.css';

const LayoutExamples: React.FC = () => {
  const [selectedLayout, setSelectedLayout] = useState<number>(1);

  const layouts = [
    {
      id: 1,
      name: "Large Single Card with Tabs",
      description: "One unified card with tabbed navigation between insight types"
    },
    {
      id: 2,
      name: "Feed-Style Scrollable List",
      description: "Timeline-style feed with chronological insights"
    },
    {
      id: 3,
      name: "Grid of Insight Cards",
      description: "Multiple cards arranged in a responsive grid"
    },
    {
      id: 4,
      name: "Dashboard Widgets",
      description: "Multiple small widgets with summary information"
    },
    {
      id: 5,
      name: "Accordion Style",
      description: "Collapsible sections for each insight type"
    },
    {
      id: 6,
      name: "Sidebar + Main Content",
      description: "Navigation sidebar with detailed main content area"
    },
    {
      id: 7,
      name: "Kanban Board Style",
      description: "Columns for different insight categories"
    },
    {
      id: 8,
      name: "Modal Overlay",
      description: "Insights displayed in overlay modals"
    },
    {
      id: 9,
      name: "Split Screen",
      description: "Two-panel layout with summary and details"
    },
    {
      id: 10,
      name: "Floating Action Panel",
      description: "Expandable panel with quick insights"
    }
  ];

  const mockInsights = [
    {
      id: 1,
      type: "nearby",
      title: "New 10-storey application filed 400m from your Queen St. project",
      description: "Competition analysis shows potential impact on market positioning",
      time: "2 hours ago",
      location: "Mississauga",
      severity: "high",
      icon: "🏗️"
    },
    {
      id: 2,
      type: "similar",
      title: "Two townhouse projects approved in Mississauga this week",
      description: "Similar built form to your application - positive precedent",
      time: "1 day ago",
      location: "Mississauga",
      severity: "medium",
      icon: "📊"
    },
    {
      id: 3,
      type: "precedent",
      title: "Height variance approved for similar project",
      description: "Committee of Adjustment decision sets favorable precedent",
      time: "3 days ago",
      location: "Toronto",
      severity: "low",
      icon: "⚖️"
    },
    {
      id: 4,
      type: "agenda",
      title: "Density study discussion on upcoming council agenda",
      description: "Could affect height/density guidelines in your area",
      time: "5 days ago",
      location: "Toronto",
      severity: "medium",
      icon: "📅"
    },
    {
      id: 5,
      type: "regional",
      title: "New growth targets announced for GTA",
      description: "Provincial policy changes may impact development potential",
      time: "1 week ago",
      location: "Ontario",
      severity: "high",
      icon: "🗺️"
    }
  ];

  const renderLayout1 = () => (
    <div className="layout-example layout-1">
      <div className="insights-header">
        <h2>Project Insights</h2>
        <div className="header-actions">
          <button className="action-btn">
            <RefreshCw size={16} />
          </button>
          <button className="action-btn">
            <Filter size={16} />
          </button>
          <button className="action-btn">
            <Settings size={16} />
          </button>
        </div>
      </div>
      
      <div className="tabs-container">
        <div className="tabs">
          <button className="tab active">Nearby Apps</button>
          <button className="tab">Similar Form</button>
          <button className="tab">Precedents</button>
          <button className="tab">Agenda</button>
          <button className="tab">Regional</button>
        </div>
      </div>

      <div className="insights-content">
        {mockInsights.slice(0, 3).map(insight => (
          <div key={insight.id} className={`insight-item ${insight.severity}`}>
            <div className="insight-icon">{insight.icon}</div>
            <div className="insight-details">
              <h4>{insight.title}</h4>
              <p>{insight.description}</p>
              <div className="insight-meta">
                <span className="time">{insight.time}</span>
                <span className="location">{insight.location}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLayout2 = () => (
    <div className="layout-example layout-2">
      <div className="insights-header">
        <h2>Project Insights</h2>
        <div className="header-actions">
          <span>View All</span>
          <button className="action-btn">
            <Settings size={16} />
          </button>
        </div>
      </div>

      <div className="feed-container">
        {mockInsights.map(insight => (
          <div key={insight.id} className={`feed-item ${insight.severity}`}>
            <div className="feed-indicator"></div>
            <div className="feed-content">
              <div className="feed-header">
                <span className="feed-type">{insight.type.toUpperCase()}</span>
                <span className="feed-time">{insight.time}</span>
              </div>
              <h4>{insight.title}</h4>
              <p>{insight.description}</p>
              <div className="feed-footer">
                <span className="feed-location">{insight.location}</span>
                {insight.type === 'similar' && <span className="similarity">85% similarity</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLayout3 = () => (
    <div className="layout-example layout-3">
      <div className="insights-header">
        <h2>Project Insights</h2>
        <button className="refresh-btn">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="grid-container">
        {mockInsights.map(insight => (
          <div key={insight.id} className={`insight-card ${insight.severity}`}>
            <div className="card-header">
              <span className="card-icon">{insight.icon}</span>
              <span className="card-type">{insight.type}</span>
            </div>
            <h4>{insight.title}</h4>
            <p>{insight.description}</p>
            <div className="card-footer">
              <span className="card-location">{insight.location}</span>
              <span className="card-time">{insight.time}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLayout4 = () => (
    <div className="layout-example layout-4">
      <div className="insights-header">
        <h2>Project Insights Dashboard</h2>
      </div>

      <div className="widgets-container">
        <div className="widget summary-widget">
          <h3>Summary</h3>
          <div className="widget-stats">
            <div className="stat">
              <span className="stat-number">5</span>
              <span className="stat-label">Active Insights</span>
            </div>
            <div className="stat">
              <span className="stat-number">2</span>
              <span className="stat-label">High Priority</span>
            </div>
          </div>
        </div>

        {['nearby', 'similar', 'precedent', 'agenda', 'regional'].map(type => {
          const insight = mockInsights.find(i => i.type === type);
          return (
            <div key={type} className="widget">
              <h3>{type.charAt(0).toUpperCase() + type.slice(1)}</h3>
              {insight && (
                <>
                  <h4>{insight.title}</h4>
                  <p>{insight.description}</p>
                  <div className="widget-meta">
                    <span>{insight.location}</span>
                    <span>{insight.time}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderLayout5 = () => (
    <div className="layout-example layout-5">
      <div className="insights-header">
        <h2>Project Insights</h2>
      </div>

      <div className="accordion-container">
        {['nearby', 'similar', 'precedent', 'agenda', 'regional'].map((type, index) => {
          const insight = mockInsights.find(i => i.type === type);
          return (
            <div key={type} className="accordion-item">
              <div className="accordion-header">
                <span className="accordion-icon">{insight?.icon}</span>
                <span className="accordion-title">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                <span className="accordion-toggle">+</span>
              </div>
              <div className="accordion-content">
                {insight && (
                  <>
                    <h4>{insight.title}</h4>
                    <p>{insight.description}</p>
                    <div className="accordion-meta">
                      <span>{insight.location}</span>
                      <span>{insight.time}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderSelectedLayout = () => {
    switch (selectedLayout) {
      case 1: return renderLayout1();
      case 2: return renderLayout2();
      case 3: return renderLayout3();
      case 4: return renderLayout4();
      case 5: return renderLayout5();
      default: return renderLayout1();
    }
  };

  return (
    <div className="layout-examples-page">
      <div className="page-header">
        <h1>Project Insights - Layout Examples</h1>
        <p>Choose your preferred layout design for the Project Insights feature</p>
      </div>

      <div className="layout-selector">
        <h3>Available Layouts</h3>
        <div className="layout-options">
          {layouts.map(layout => (
            <button
              key={layout.id}
              className={`layout-option ${selectedLayout === layout.id ? 'active' : ''}`}
              onClick={() => setSelectedLayout(layout.id)}
            >
              <div className="option-number">{layout.id}</div>
              <div className="option-details">
                <h4>{layout.name}</h4>
                <p>{layout.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="preview-section">
        <h3>Preview</h3>
        <div className="preview-container">
          {renderSelectedLayout()}
        </div>
      </div>

      <div className="selection-info">
        <h3>Selected: Layout {selectedLayout}</h3>
        <p>{layouts.find(l => l.id === selectedLayout)?.description}</p>
        <button className="select-layout-btn">
          <Eye size={16} />
          Select This Layout
        </button>
      </div>
    </div>
  );
};

export default LayoutExamples;
